import { useState } from "react";
import { Building2, Layers, Users, ChevronRight, ChevronDown } from "lucide-react";
import { Badge, cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from "@fox1ck-org/ui";
import type { OrgUnit, OrgUnitType } from "@/types/org";

const typeIcons: Record<OrgUnitType, React.ComponentType<{ className?: string }>> = {
  company: Building2,
  department: Layers,
  team: Users,
};

interface OrgTreeNodeProps {
  node: OrgUnit;
  selectedId?: string;
  depth: number;
  onSelect: (id: string) => void;
}

export function OrgTreeNode({ node, selectedId, depth, onSelect }: OrgTreeNodeProps) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = node.id === selectedId;
  const Icon = typeIcons[node.type] ?? Users;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          "flex items-center gap-1 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors",
          isSelected
            ? "bg-sidebar-accent text-sidebar-accent-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => onSelect(node.id)}
      >
        {hasChildren ? (
          <CollapsibleTrigger
            asChild
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <button className="shrink-0 p-0.5 rounded hover:bg-muted-foreground/10">
              {open ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          </CollapsibleTrigger>
        ) : (
          <span className="w-[18px] shrink-0" />
        )}
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate flex-1">{node.name}</span>
        {node.memberCount != null && node.memberCount > 0 && (
          <Badge
            variant="neutral"
            className="h-4 min-w-[18px] px-1 text-[10px] font-medium leading-none"
          >
            {node.memberCount}
          </Badge>
        )}
      </div>

      {hasChildren && (
        <CollapsibleContent>
          {node.children!.map((child) => (
            <OrgTreeNode
              key={child.id}
              node={child}
              selectedId={selectedId}
              depth={depth + 1}
              onSelect={onSelect}
            />
          ))}
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
